<template>
  <div class="highchart-container">
    <highcharts
      :options="chartOptions"
    />
  </div>
</template>

<script>
import moment from 'moment';
import unitPreferencesMixin from '@shared/mixins/unitPreferencesMixin';

import convertToCelsius from '@shared/lib/converter/convertToCelsius';

export default {
  name: 'DailyDeltaPGraph',
  mixins: [unitPreferencesMixin],
  props: {
    dailyStats: Array,
    pressureConversion: Number,
  },
  computed: {
    graphData() {
      const {
        partnerSettings: {
          TEMP_UNIT = '°F',
          DATE_FORMAT = 'MM-DD-YYYY',
          PRESSURE_UNIT = 'inH2O',
        },
      } = this.$store.state.admin;

      const columnDate = [];
      const coolAvgTemp = [];
      const heatAvgTemp = [];
      const maxOATemp = [];
      const minOATemp = [];
      this.dailyStats.forEach((c) => {
        const {
          localDate,
          deltaPs,
          maxTempAPI,
          minTempAPI,
        } = c;
        if (deltaPs) {
          if (deltaPs.Cool) {
            const {
              Cool: cool,
            } = deltaPs;

            const {
              avg,
            } = cool;
            let pChange = avg;
            if (PRESSURE_UNIT === 'inH2O') {
              pChange /= this.pressureConversion;
            }
            coolAvgTemp.push(pChange);
          } else {
            coolAvgTemp.push(null);
          }

          if (deltaPs.Heat) {
            const {
              Heat: heat,
            } = deltaPs;

            const {
              avg,
            } = heat;
            let pChange = avg;
            if (PRESSURE_UNIT === 'inH2O') {
              pChange /= this.pressureConversion;
            }
            heatAvgTemp.push(pChange);
          } else {
            heatAvgTemp.push(null);
          }
        } else {
          coolAvgTemp.push(null);
          heatAvgTemp.push(null);
        }

        let max = maxTempAPI;
        if (TEMP_UNIT === '°C') {
          max = convertToCelsius(max);
        }
        let min = minTempAPI;
        if (TEMP_UNIT === '°C') {
          min = convertToCelsius(min);
        }

        let date = moment(localDate).format('M/D/YYYY');
        if (DATE_FORMAT === 'DD-MM-YYYY') {
          date = moment(localDate).format('D/M/YYYY');
        }
        maxOATemp.push(max);
        minOATemp.push(min);
        columnDate.push(date);
      });

      return {
        columnDate,
        coolAvgTemp,
        heatAvgTemp,
        minOATemp,
        maxOATemp,
      };
    },
    chartOptions() {
      const {
        partnerSettings: {
          PRESSURE_UNIT = 'inH2O',
        },
      } = this.$store.state.admin;

      const {
        tempFormat,
        pressureFormat,
      } = this;

      const {
        columnDate,
        coolAvgTemp,
        heatAvgTemp,
        minOATemp,
        maxOATemp,
      } = this.graphData;

      return {
        chart: {
          zoomType: 'x',
          height: 400,
          backgroundColor: '#0f1734',
        },
        credits: {
          enabled: false,
        },
        title: {
          text: 'Delta P',
          style: {
            color: 'white',
          },
        },
        tooltip: {
          useHTML: true,
          nullFormat: 'Value is not available.',
          formatter() {
            const value = PRESSURE_UNIT === 'inH2O' ? this.y.toFixed(2) : this.y;
            return `${this.x}<br/>`
              + `${this.series.name}: <b>${value}</b>`;
          },
        },
        xAxis: [{
          categories: columnDate,
          reversed: new Date(columnDate[0]) > new Date(columnDate[columnDate.length - 1]),
          title: {
            text: 'Days',
            style: {
              color: 'white',
            },
          },
          labels: {
            style: {
              color: 'white',
            },
          },
        }],
        yAxis: [{
          labels: {
            format: `{value}${pressureFormat}`,
            style: {
              color: 'white',
            },
          },
          title: {
            text: 'Pressure',
            style: {
              color: 'white',
            },
          },
          min: Math.min(...heatAvgTemp, ...coolAvgTemp) - (PRESSURE_UNIT === 'inH2O' ? 0.4 : 4),
          max: Math.max(...heatAvgTemp, ...coolAvgTemp) + (PRESSURE_UNIT === 'inH2O' ? 0.4 : 4),
          opposite: false,
        }, {
          labels: {
            format: `{value}${tempFormat}`,
            style: {
              color: 'white',
            },
          },
          title: {
            text: 'Outside Air Temperature',
            style: {
              color: 'white',
            },
          },
          min: Math.min(...minOATemp, ...maxOATemp) - 4,
          max: Math.max(...minOATemp, ...maxOATemp) + 4,
          opposite: true,
        }],
        legend: {
          shadow: false,
          align: 'center',
          verticalAlign: 'bottom',
          x: 0,
          y: 0,
          itemStyle: {
            color: 'white',
          },
        },
        series: [{
          type: 'scatter',
          yAxis: 0,
          xAxis: 0,
          name: 'Cool Avg',
          color: '#4979FF',
          data: coolAvgTemp,
        }, {
          type: 'scatter',
          yAxis: 0,
          xAxis: 0,
          name: 'Heat Avg',
          color: '#F7433A',
          data: heatAvgTemp,
        }, {
          type: 'scatter',
          yAxis: 1,
          xAxis: 0,
          name: 'Max OA T',
          color: '#FFA500',
          data: maxOATemp,
        }, {
          type: 'scatter',
          yAxis: 1,
          xAxis: 0,
          name: 'Min OA T',
          color: '#ffdb9a',
          data: minOATemp,
        }],
      };
    },
  },
};
</script>

<style lang="scss">

</style>
